import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import ProgressBar from '../components/ui/ProgressBar'
import { StatusBadge } from '../components/ui/StatusBadge'
import FileList from '../components/files/FileList'
import FileUpload from '../components/files/FileUpload'
import Comments from '../components/files/Comments'
import { LogOut, ChevronDown, ChevronRight, FileText, MessageSquare, Building2, CheckSquare, Paperclip, ClipboardList, LayoutList, User } from 'lucide-react'
import TaskDashboard from '../components/tasks/TaskDashboard'

const PROJECT_ICONS = ['🌍','🏭','🎯']
const BU_STAGE_ICONS = ['📊','👥','⚔️','🏢','🗺️']

const STATUS_LABELS = {
  pending: 'Pendiente',
  in_progress: 'En Progreso',
  review: 'En Revisión',
  completed: 'Completado',
  blocked: 'Bloqueado',
}

const PRIORITY_COLORS = {
  high: 'text-red-600 bg-red-50',
  medium: 'text-yellow-600 bg-yellow-50',
  low: 'text-green-600 bg-green-50',
}

const PRIORITY_LABELS = { high: 'Alta', medium: 'Media', low: 'Baja' }

export default function ClientPortalPage() {
  const { id }       = useParams()
  const { profile, signOut } = useAuth()
  const navigate     = useNavigate()
  const [consultancy, setConsultancy]       = useState(null)
  const [projectPhases, setProjectPhases]   = useState([])
  const [bus, setBus]                       = useState([])
  const [aiReports, setAiReports]           = useState([])
  const [loading, setLoading]               = useState(true)
  const [expandedProjPhase, setExpandedProjPhase] = useState({})
  const [expandedBu, setExpandedBu]         = useState({})
  const [expandedPhase, setExpandedPhase]   = useState({})
  const [expandedSection, setExpandedSection] = useState({})
  const [expandedTask, setExpandedTask]     = useState({})
  const [expandedReport, setExpandedReport] = useState({})
  const [reportApprovals, setReportApprovals] = useState({})
  const [activeTab, setActiveTab]           = useState('progress')
  const [phaseTab, setPhaseTab]             = useState({}) // { [phaseId]: 'sections' | 'files' }
  const [profileMap, setProfileMap]         = useState({}) // { [id]: { id, full_name } }
  const [allTasks, setAllTasks]             = useState([])
  const [myTasks, setMyTasks]               = useState([])
  const [drlItems, setDrlItems]             = useState([])

  useEffect(() => {
    if (profile?.id) fetchData()
    else setLoading(false)
  }, [profile?.id])

  async function fetchData() {
    setLoading(true)
    try {
      const t = () => new Promise(resolve => setTimeout(() => resolve({ data: null, error: 'timeout' }), 8000))

      const { data: access } = await Promise.race([
        supabase.from('consultancy_users').select('consultancy_id').eq('user_id', profile.id).single(),
        t()
      ])

      const cId = id ?? access?.consultancy_id
      if (!cId) { setLoading(false); return }

      const TASKS_SELECT = 'id, name, status, description, priority, assignee_id'
      const SECTIONS_SELECT = `id, name, status, description, assignee_id, tasks(${TASKS_SELECT})`

      const [{ data: cons }, { data: buList }, { data: projPhases }, { data: reports }, { data: profs }, { data: drl }] = await Promise.all([
        Promise.race([supabase.from('consultancies').select('*').eq('id', cId).single(), t()]),
        Promise.race([
          supabase.from('business_units')
            .select(`*, phases(id, name, status, progress, phase_order, sections(${SECTIONS_SELECT}))`)
            .eq('consultancy_id', cId)
            .order('bu_order'),
          t()
        ]),
        Promise.race([
          supabase.from('phases')
            .select(`id, name, status, progress, phase_order, sections(${SECTIONS_SELECT})`)
            .eq('consultancy_id', cId)
            .is('business_unit_id', null)
            .order('phase_order'),
          t()
        ]),
        Promise.race([
          supabase.from('ai_reports').select('*').eq('consultancy_id', cId)
            .order('created_at', { ascending: false }),
          t()
        ]),
        Promise.race([
          supabase.from('profiles').select('id, full_name'),
          t()
        ]),
        Promise.race([
          supabase.from('solicitudes_informacion')
            .select('id, descripcion, categoria, estado, orden')
            .eq('consultancy_id', cId)
            .order('categoria')
            .order('orden'),
          t()
        ]),
      ])
      // Build a quick lookup map { id -> profile } using local variable (no state timing issues)
      const map = Object.fromEntries((profs ?? []).map(p => [p.id, p]))
      console.log('[Portal] profiles loaded:', (profs ?? []).length)

      // Collect all section IDs and load tasks directly with 2-level assignee join
      // (avoids the 4-level deep join that fails silently)
      const allPhases = [
        ...(projPhases ?? []),
        ...(buList ?? []).flatMap(bu => bu.phases ?? []),
      ]
      const sectionLookup = {} // sectionId -> { name, assignee_id, phaseName }
      for (const phase of allPhases) {
        for (const section of phase.sections ?? []) {
          sectionLookup[section.id] = { name: section.name, assignee_id: section.assignee_id, phaseName: phase.name }
        }
      }
      const sectionIds = Object.keys(sectionLookup)

      // Direct tasks query with 2-level join — cleaner than nested phases→sections→tasks→profiles
      const { data: taskRows } = sectionIds.length > 0
        ? await supabase
            .from('tasks')
            .select('id, name, status, description, priority, assignee_id, section_id, assignee:profiles!tasks_assignee_id_fkey(id, full_name)')
            .in('section_id', sectionIds)
        : { data: [] }
      console.log('[Portal] tasks loaded:', (taskRows ?? []).length, '| with assignee:', (taskRows ?? []).filter(t => t.assignee).length)

      // Build enriched flat task arrays
      const allFlat = []
      const myFlat  = []
      for (const task of taskRows ?? []) {
        const sec = sectionLookup[task.section_id]
        const enriched = {
          ...task,
          assignee:        task.assignee        ?? (task.assignee_id    ? (map[task.assignee_id]    ?? null) : null),
          sectionAssignee: sec?.assignee_id     ? (map[sec.assignee_id] ?? null) : null,
          sectionName: sec?.name     ?? '',
          phaseName:   sec?.phaseName ?? '',
        }
        allFlat.push(enriched)
        if (task.assignee_id === profile?.id) myFlat.push(enriched)
      }
      console.log('[Portal] allFlat with assignee:', allFlat.filter(t => t.assignee).length, 'of', allFlat.length)

      setConsultancy(cons)
      setBus(buList ?? [])
      setProjectPhases(projPhases ?? [])
      setAiReports(reports ?? [])
      setProfileMap(map)
      setAllTasks(allFlat)
      setMyTasks(myFlat)
      setDrlItems(drl ?? [])
    } catch (e) {
      console.error('Error loading portal:', e)
    } finally {
      setLoading(false)
    }
  }

  async function handleSignOut() { await signOut(); navigate('/login') }

  function toggleProjPhase(id) { setExpandedProjPhase(e => ({ ...e, [id]: !e[id] })) }
  function toggleBu(id)         { setExpandedBu(e => ({ ...e, [id]: !e[id] })) }
  function togglePhase(id)      { setExpandedPhase(e => ({ ...e, [id]: !e[id] })) }
  function toggleSection(id)    { setExpandedSection(e => ({ ...e, [id]: !e[id] })) }
  function toggleTask(id)       { setExpandedTask(e => ({ ...e, [id]: !e[id] })) }
  function toggleReport(id)     { setExpandedReport(e => ({ ...e, [id]: !e[id] })) }

  function downloadReport(report) {
    const blob = new Blob([report.content], { type: 'text/plain;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    const date = new Date(report.created_at).toISOString().split('T')[0]
    a.download = `informe-${report.report_type}-${date}.txt`
    a.click()
  }

  async function approveReport(reportId, approved) {
    setReportApprovals(prev => ({ ...prev, [reportId]: approved ? 'approved' : 'rejected' }))
    // Save approval as a comment
    await supabase.from('ai_reports').update({
      metadata: { client_approved: approved, approved_by: profile.id, approved_at: new Date().toISOString() }
    }).eq('id', reportId)
  }

  const REPORT_TYPE_LABELS = {
    strategic_plan: 'Plan Estratégico',
    agent_generated: 'Informe de Fase',
    summary: 'Resumen Ejecutivo',
    executive: 'Informe Ejecutivo',
    meeting_notes: 'Notas de Reunión',
    agent_turn: 'Análisis del Agente',
  }

  if (loading) return (
    <div className="min-h-screen bg-brand-700 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent" />
    </div>
  )

  if (!consultancy) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <p className="text-gray-500">No tienes una consultoría asignada.</p>
        <button onClick={handleSignOut} className="mt-4 btn-secondary">Cerrar sesión</button>
      </div>
    </div>
  )

  const allProgresses = [
    ...projectPhases.map(p => p.progress ?? 0),
    ...bus.flatMap(bu => (bu.phases ?? []).map(p => p.progress ?? 0)),
  ]
  const overallProgress = allProgresses.length
    ? Math.round(allProgresses.reduce((s, n) => s + n, 0) / allProgresses.length)
    : 0

  // allTasks and myTasks are now built inside fetchData with direct map lookup (no React state timing issues)

  const DRL_CATEGORIAS = ['Financiero', 'Comercial', 'Marketing', 'Operaciones', 'RRHH', 'Estratégico', 'Otro']
  const DRL_CAT_ICONS  = { 'Corporativo': '🎯', 'Unidades de negocio': '🏢', 'Financiero': '💰', 'Comercial': '📈', 'Operaciones': '⚙️', 'Marketing': '📣', 'RRHH': '👥' }
  const DRL_ESTADO_COLORS = {
    'pending':         { bg: '#FEF3C7', text: '#92400E', dot: '#D97706' },
    'Pendiente':       { bg: '#FEF3C7', text: '#92400E', dot: '#D97706' },
    'En revisión':     { bg: '#DBEAFE', text: '#1E40AF', dot: '#3B82F6' },
    'Entrega parcial': { bg: '#FEF9C3', text: '#713F12', dot: '#CA8A04' },
    'Recibido':        { bg: '#D1FAE5', text: '#065F46', dot: '#10B981' },
    'No aplica':       { bg: '#F3F4F6', text: '#6B7280', dot: '#9CA3AF' },
    'No existe':       { bg: '#FEE2E2', text: '#991B1B', dot: '#EF4444' },
  }
  const DRL_ESTADO_LABEL = {
    'pending': 'Pendiente',
  }

  // Group DRL items by category, preserving order
  const drlByCategory = DRL_CATEGORIAS.reduce((acc, cat) => {
    const items = drlItems.filter(i => i.categoria === cat)
    if (items.length > 0) acc[cat] = items
    return acc
  }, {})

  // DRL summary counts
  const drlTotal    = drlItems.length
  const drlRecibido = drlItems.filter(i => i.estado === 'Recibido').length
  const drlPct      = drlTotal > 0 ? Math.round((drlRecibido / drlTotal) * 100) : 0

  const tabs = [
    { id: 'mytasks',  label: `Mis Tareas${myTasks.length > 0 ? ` (${myTasks.length})` : ''}`, icon: ClipboardList },
    { id: 'tareas',   label: 'Todas las Tareas', icon: LayoutList },
    { id: 'progress', label: 'Avance del Proyecto',  icon: ChevronRight },
    { id: 'files',    label: 'Documentos',            icon: FileText     },
    { id: 'comments', label: 'Comentarios',           icon: MessageSquare },
  ]

  // Renders tasks within a section — shows all tasks
  function SectionTasks({ section }) {
    const tasks = section.tasks ?? []
    if (tasks.length === 0) return null
    return (
      <div className="space-y-1 px-10 py-2">
        {tasks.map(task => (
          <div key={task.id} className="bg-white rounded-lg border border-gray-100 overflow-hidden">
            {/* Task row */}
            <button
              onClick={() => toggleTask(task.id)}
              className="w-full flex items-start gap-3 p-3 text-left hover:bg-gray-50 transition-colors"
            >
              <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                task.status === 'completed' ? 'bg-green-500 border-green-500' : 'border-gray-300'
              }`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${task.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                  {task.name}
                </p>
                {task.description && (
                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{task.description}</p>
                )}
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-xs text-gray-400">{STATUS_LABELS[task.status] ?? task.status}</span>
                  {task.priority && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${PRIORITY_COLORS[task.priority] ?? ''}`}>
                      {PRIORITY_LABELS[task.priority] ?? task.priority}
                    </span>
                  )}
                  {task.assignee && (
                    <span className="flex items-center gap-1 text-xs text-brand-600">
                      <User size={10} /> {task.assignee.full_name}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0 text-brand-500">
                <Paperclip size={13} />
                <span className="text-xs">{expandedTask[task.id] ? 'Ocultar' : 'Archivos'}</span>
                {expandedTask[task.id]
                  ? <ChevronDown size={13} />
                  : <ChevronRight size={13} />}
              </div>
            </button>

            {/* File upload/list area */}
            {expandedTask[task.id] && (
              <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Archivos de esta tarea</p>
                <FileUpload entityType="task" entityId={task.id} onUploaded={() => {}} />
                <FileList entityType="task" entityId={task.id} />
              </div>
            )}
          </div>
        ))}
      </div>
    )
  }

  // Renders sections (with toggle to show tasks)
  function PhaseSections({ sections, indent = 'px-8' }) {
    const [sectionTab, setSectionTab] = useState({}) // { [sectionId]: 'tasks' | 'files' }

    return (
      <div className={`bg-gray-50 ${indent} py-2 space-y-1 border-b border-gray-100`}>
        {(sections ?? []).map(sec => {
          const tab = sectionTab[sec.id] ?? 'tasks'
          return (
            <div key={sec.id} className="bg-white rounded-lg border border-gray-100 overflow-hidden">
              <button
                onClick={() => toggleSection(sec.id)}
                className="w-full flex items-center justify-between py-2 px-3 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <CheckSquare size={14} className="text-brand-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <span className="text-sm text-gray-700 font-medium">{sec.name}</span>
                    {sec.description && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{sec.description}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  <StatusBadge status={sec.status} />
                  <span className="text-xs text-gray-400">
                    {(sec.tasks ?? []).length} tarea{(sec.tasks ?? []).length !== 1 ? 's' : ''}
                  </span>
                  {expandedSection[sec.id]
                    ? <ChevronDown size={13} className="text-gray-400" />
                    : <ChevronRight size={13} className="text-gray-400" />}
                </div>
              </button>

              {expandedSection[sec.id] && (
                <>
                  {/* Mini-tabs: Tareas / Archivos */}
                  <div className="flex border-t border-gray-100 bg-gray-50">
                    {['tasks', 'files'].map(t => (
                      <button
                        key={t}
                        onClick={() => setSectionTab(prev => ({ ...prev, [sec.id]: t }))}
                        className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                          tab === t
                            ? 'border-brand-600 text-brand-700 bg-white'
                            : 'border-transparent text-gray-400 hover:text-gray-600'
                        }`}
                      >
                        {t === 'tasks' ? <><CheckSquare size={11} /> Tareas</> : <><Paperclip size={11} /> Archivos</>}
                      </button>
                    ))}
                  </div>

                  {tab === 'tasks' && <SectionTasks section={sec} />}

                  {tab === 'files' && (
                    <div className="p-4 space-y-3">
                      <FileUpload entityType="section" entityId={sec.id} onUploaded={() => {}} />
                      <FileList entityType="section" entityId={sec.id} />
                    </div>
                  )}
                </>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  // Read-only DRL view shown inside Modelo de Negocio phase in client portal
  function DRLClientView({ items, byCategory, estadoColors, estadoLabel = {}, catIcons, total, recibido, pct }) {
    return (
      <div className="bg-gray-50 px-5 py-4 space-y-3">
        {/* Mini summary */}
        <div className="flex items-center justify-between bg-white rounded-lg border border-gray-100 px-4 py-3">
          <div>
            <p className="text-xs font-semibold text-gray-700">Lista de Requerimiento de Información</p>
            <p className="text-xs text-gray-400 mt-0.5">{recibido} de {total} ítems recibidos</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-24 bg-gray-100 rounded-full h-1.5">
              <div className="h-1.5 rounded-full bg-brand-600 transition-all duration-300" style={{ width: `${pct}%` }} />
            </div>
            <span className="text-sm font-bold text-brand-700">{pct}%</span>
          </div>
        </div>
        {/* Legend */}
        <div className="flex flex-wrap gap-3 px-1">
          {[['Pendiente','#D97706'],['En revisión','#3B82F6'],['Entrega parcial','#CA8A04'],['Recibido','#10B981'],['No aplica','#9CA3AF'],['No existe','#EF4444']].map(([label, dot]) => (
            <div key={label} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: dot }} />
              <span className="text-xs text-gray-500">{label}</span>
            </div>
          ))}
        </div>
        {/* Category groups */}
        {items.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <ClipboardList size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">El equipo consultor configurará la lista pronto.</p>
          </div>
        ) : (
          Object.entries(byCategory).map(([cat, catItems]) => {
            const catRecibidos = catItems.filter(i => i.estado === 'Recibido').length
            const catPct = Math.round((catRecibidos / catItems.length) * 100)
            return (
              <div key={cat} className="bg-white rounded-lg border border-gray-100 overflow-hidden">
                <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span>{catIcons[cat] ?? '📄'}</span>
                    <span className="text-sm font-semibold text-gray-800">{cat}</span>
                    <span className="text-xs text-gray-400">{catItems.length} ítems</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-14 bg-gray-200 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full transition-all" style={{ width: `${catPct}%`, backgroundColor: '#059669' }} />
                    </div>
                    <span className="text-xs text-gray-500 w-7 text-right">{catPct}%</span>
                  </div>
                </div>
                <div className="divide-y divide-gray-50">
                  {catItems.map(item => {
                    const colors = estadoColors[item.estado] ?? estadoColors['Pendiente']
                    return (
                      <div key={item.id} className="flex items-start gap-3 px-4 py-2.5">
                        <div className="mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: colors.dot }} />
                        <p className="flex-1 text-sm text-gray-800">{item.descripcion}</p>
                        <span className="flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: colors.bg, color: colors.text }}>
                          {estadoLabel[item.estado] ?? item.estado}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })
        )}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-brand-700 text-white px-6 py-4 flex items-center justify-between">
        <div>
          <div className="text-xs text-brand-300 font-semibold uppercase tracking-widest">Next Valley Consultants</div>
          <div className="text-lg font-bold">Portal del Cliente</div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-brand-200">Hola, {profile.full_name}</div>
          <button onClick={handleSignOut} className="flex items-center gap-1.5 text-sm text-brand-200 hover:text-white transition-colors">
            <LogOut size={15} /> Salir
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Project card */}
        <div className="card">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-brand-700 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
              {consultancy.client_company.charAt(0)}
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">{consultancy.name}</h1>
              <p className="text-gray-500 text-sm">{consultancy.client_company}</p>
              {consultancy.description && <p className="text-sm text-gray-500 mt-2">{consultancy.description}</p>}
            </div>
          </div>
          <div className="mt-4">
            <div className="flex justify-between text-sm text-gray-500 mb-2">
              <span>Progreso global del proyecto</span>
              <span className="font-semibold text-brand-700">{overallProgress}%</span>
            </div>
            <ProgressBar value={overallProgress} size="lg" showLabel={false} />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
          {tabs.map(({ id: tid, label, icon: Icon }) => (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === tid ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>

        {/* ── Mis Tareas tab ── */}
        {activeTab === 'mytasks' && (
          <div className="space-y-4">
            {myTasks.length === 0 ? (
              <div className="card text-center py-12">
                <ClipboardList size={40} className="mx-auto mb-3 text-gray-300" />
                <p className="font-medium text-gray-500">No tienes tareas asignadas aún</p>
                <p className="text-sm text-gray-400 mt-1">Cuando el equipo te asigne una tarea aparecerá aquí.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {myTasks.map(task => (
                  <div key={task.id} className="card p-0 overflow-hidden">
                    <button
                      onClick={() => toggleTask(task.id)}
                      className="w-full flex items-start gap-3 p-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className={`mt-1 w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                        task.status === 'completed' ? 'bg-green-500 border-green-500' : 'border-gray-300'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold ${task.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                          {task.name}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">{task.phaseName} › {task.sectionName}</p>
                        {task.description && (
                          <p className="text-xs text-gray-500 mt-1">{task.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          <span className="text-xs text-gray-400">{STATUS_LABELS[task.status] ?? task.status}</span>
                          {task.priority && (
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${PRIORITY_COLORS[task.priority] ?? ''}`}>
                              {PRIORITY_LABELS[task.priority] ?? task.priority}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0 text-brand-500">
                        <Paperclip size={13} />
                        <span className="text-xs">{expandedTask[task.id] ? 'Ocultar' : 'Subir archivos'}</span>
                        {expandedTask[task.id] ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                      </div>
                    </button>
                    {expandedTask[task.id] && (
                      <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Archivos de esta tarea</p>
                        <FileUpload entityType="task" entityId={task.id} onUploaded={() => {}} />
                        <FileList entityType="task" entityId={task.id} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Todas las Tareas tab ── */}
        {activeTab === 'tareas' && (
          <TaskDashboard tasks={allTasks} />
        )}

        {activeTab === 'progress' && (
          <div className="space-y-4">

            {/* Project-level phases (1-3) */}
            {projectPhases.length > 0 && (
              <div className="card p-0 overflow-hidden">
                <div className="px-5 py-3 bg-brand-50 border-b border-brand-100">
                  <span className="text-xs font-semibold text-brand-700 uppercase tracking-wide">Etapas del Proyecto</span>
                </div>
                {projectPhases.map((phase, idx) => (
                  <div key={phase.id}>
                    <button
                      onClick={() => toggleProjPhase(phase.id)}
                      className="w-full flex items-center gap-4 px-5 py-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-50 last:border-0"
                    >
                      <span className="text-lg flex-shrink-0">{PROJECT_ICONS[idx] ?? '📋'}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-gray-800">{phase.name}</span>
                          <StatusBadge status={phase.status} />
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 max-w-xs">
                            <ProgressBar value={phase.progress ?? 0} size="sm" showLabel={false} />
                          </div>
                          <span className="text-xs text-gray-400">{phase.progress ?? 0}%</span>
                        </div>
                      </div>
                      {expandedProjPhase[phase.id]
                        ? <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />
                        : <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />}
                    </button>
                    {expandedProjPhase[phase.id] && (() => {
                      const isModeloNegocio = phase.phase_order === 0 ||
                        phase.name?.toLowerCase().includes('modelo de negocio')
                      const defaultTab = isModeloNegocio ? 'drl' : 'sections'
                      const currentTab = phaseTab[phase.id] ?? defaultTab
                      return (
                        <>
                          <div className="flex border-t border-gray-100 bg-gray-50">
                            {(isModeloNegocio
                              ? ['drl', 'files']
                              : ['sections', 'files']
                            ).map(t => (
                              <button key={t}
                                onClick={() => setPhaseTab(prev => ({ ...prev, [phase.id]: t }))}
                                className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                                  currentTab === t
                                    ? 'border-brand-600 text-brand-700 bg-white'
                                    : 'border-transparent text-gray-400 hover:text-gray-600'
                                }`}>
                                {t === 'sections' && <><CheckSquare size={11} /> Secciones</>}
                                {t === 'drl'      && <><ClipboardList size={11} /> Lista de Información</>}
                                {t === 'files'    && <><Paperclip size={11} /> Archivos</>}
                              </button>
                            ))}
                          </div>
                          {currentTab === 'sections' && <PhaseSections sections={phase.sections} indent="px-5" />}
                          {currentTab === 'drl' && (
                            <DRLClientView items={drlItems} byCategory={drlByCategory} estadoColors={DRL_ESTADO_COLORS} estadoLabel={DRL_ESTADO_LABEL} catIcons={DRL_CAT_ICONS} total={drlTotal} recibido={drlRecibido} pct={drlPct} />
                          )}
                          {currentTab === 'files' && (
                            <div className="p-4 space-y-3">
                              <FileUpload entityType="phase" entityId={phase.id} onUploaded={() => {}} />
                              <FileList entityType="phase" entityId={phase.id} />
                            </div>
                          )}
                        </>
                      )
                    })()}
                  </div>
                ))}
              </div>
            )}

            {/* BU-level phases */}
            {bus.map(bu => {
              const buPhases = (bu.phases ?? []).sort((a, b) => a.phase_order - b.phase_order)
              const buProgress = buPhases.length
                ? Math.round(buPhases.reduce((s, p) => s + (p.progress ?? 0), 0) / buPhases.length)
                : 0

              return (
                <div key={bu.id} className="card p-0 overflow-hidden">
                  {/* BU header */}
                  <button
                    onClick={() => toggleBu(bu.id)}
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors text-left"
                  >
                    <Building2 size={18} className="text-brand-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="font-semibold text-gray-900">{bu.name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 max-w-xs">
                          <ProgressBar value={buProgress} size="sm" showLabel={false} />
                        </div>
                        <span className="text-xs text-gray-400">{buProgress}%</span>
                      </div>
                    </div>
                    {expandedBu[bu.id]
                      ? <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />
                      : <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />}
                  </button>

                  {/* Phases */}
                  {expandedBu[bu.id] && (
                    <div className="border-t border-gray-100">
                      {buPhases.map((phase, idx) => (
                        <div key={phase.id}>
                          <button
                            onClick={() => togglePhase(phase.id)}
                            className="w-full flex items-center gap-4 px-6 py-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-50 last:border-0"
                          >
                            <span className="text-lg flex-shrink-0">{BU_STAGE_ICONS[idx] ?? '📋'}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-medium text-gray-800">{phase.name}</span>
                                <StatusBadge status={phase.status} />
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="flex-1 max-w-xs">
                                  <ProgressBar value={phase.progress ?? 0} size="sm" showLabel={false} />
                                </div>
                                <span className="text-xs text-gray-400">{phase.progress ?? 0}%</span>
                              </div>
                            </div>
                            {expandedPhase[phase.id]
                              ? <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />
                              : <ChevronRight size={14} className="text-gray-400 flex-shrink-0" />}
                          </button>

                          {/* Sections + Files tabs */}
                          {expandedPhase[phase.id] && (
                            <>
                              <div className="flex border-t border-gray-100 bg-gray-50">
                                {['sections', 'files'].map(t => (
                                  <button key={t}
                                    onClick={() => setPhaseTab(prev => ({ ...prev, [phase.id]: t }))}
                                    className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                                      (phaseTab[phase.id] ?? 'sections') === t
                                        ? 'border-brand-600 text-brand-700 bg-white'
                                        : 'border-transparent text-gray-400 hover:text-gray-600'
                                    }`}>
                                    {t === 'sections' ? <><CheckSquare size={11} /> Secciones</> : <><Paperclip size={11} /> Archivos</>}
                                  </button>
                                ))}
                              </div>
                              {(phaseTab[phase.id] ?? 'sections') === 'sections'
                                ? <PhaseSections sections={phase.sections} indent="px-6" />
                                : <div className="p-4 space-y-3">
                                    <FileUpload entityType="phase" entityId={phase.id} onUploaded={() => {}} />
                                    <FileList entityType="phase" entityId={phase.id} />
                                  </div>
                              }
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}

            {bus.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <Building2 size={40} className="mx-auto mb-3 opacity-20" />
                <p>El proyecto aún no tiene unidades de negocio configuradas.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'files' && (
          <FileList entityType="consultancy" entityId={consultancy.id} />
        )}

        {activeTab === 'comments' && (
          <Comments entityType="consultancy" entityId={consultancy.id} />
        )}

      </div>
    </div>
  )
}
